import { useGlossary } from '../../contexts/GlossaryContext';
import type { SevaBooking } from '../../lib/types';

const BASE_KRAMA_PHASES = [
  { key: 'PENDING_RAJA_APPROVAL', icon: '⚖️', labelKey: 'seva.gigs.krama.pending_raja'  },
  { key: 'ASSIGNMENT',            icon: '📋', labelKey: 'seva.gigs.krama.assignment'      },
  { key: 'EXECUTION',             icon: '⚙️', labelKey: 'seva.gigs.krama.execution'       },
  { key: 'REVIEW',                icon: '🔍', labelKey: 'seva.gigs.krama.review'           },
  { key: 'COMPLETE',              icon: '✅', labelKey: 'seva.gigs.krama.complete'         },
] as const;

const INTRO_PHASE  = { key: 'INTRO_CALL', icon: '📞', labelKey: 'seva.gigs.krama.intro_call' } as const;
const RECYCLED_PHASE = { key: 'RECYCLED', icon: '🔄', labelKey: 'seva.gigs.krama.recycled' } as const;

interface Props {
  booking: SevaBooking;
  /** Consumer submits review form — only shown in REVIEW phase */
  onSubmitReview?: () => void;
}

export default function BookingKramaTracker({ booking, onSubmitReview }: Props) {
  const { t } = useGlossary();
  const current = booking.krama_phase;
  const isRecycled = current === 'RECYCLED';

  const hasIntro = current === 'INTRO_CALL' ||
    booking.phase_history.some((h) => h.phase === 'INTRO_CALL');

  const phases = [
    ...(hasIntro ? [INTRO_PHASE] : []),
    ...BASE_KRAMA_PHASES,
    ...(isRecycled ? [RECYCLED_PHASE] : []),
  ];
  const currentIdx = phases.findIndex((p) => p.key === current);

  return (
    <section className="seva-krama-tracker" aria-label={t('seva.gigs.krama.label')}>
      <h4 className="seva-krama-tracker__title">{t('seva.gigs.krama.label')}</h4>

      <ol className="seva-krama-tracker__steps">
        {phases.map((phase, idx) => {
          const status =
            idx < currentIdx ? 'complete'
            : idx === currentIdx ? 'current'
            : 'pending';
          return (
            <li
              key={phase.key}
              className={`seva-krama-step seva-krama-step--${status}`}
              aria-current={status === 'current' ? 'step' : undefined}
            >
              <span className="seva-krama-step__icon">{phase.icon}</span>
              <span className="seva-krama-step__label">{t(phase.labelKey)}</span>
              {status === 'complete' && <span className="seva-krama-step__done">✓</span>}
            </li>
          );
        })}
      </ol>

      {/* Phase-specific action prompts */}
      {current === 'INTRO_CALL' && (
        <p className="seva-krama-tracker__note seva-krama-tracker__note--intro">
          📞 {t('seva.gigs.krama.intro_call.note')}
        </p>
      )}

      {current === 'PENDING_RAJA_APPROVAL' && (
        <p className="seva-krama-tracker__note seva-krama-tracker__note--raja">
          ⚖️ {t('seva.gigs.krama.pending_raja.note')}
        </p>
      )}

      {current === 'REVIEW' && onSubmitReview && (
        <button className="seva-btn seva-btn--primary seva-krama-tracker__cta" onClick={onSubmitReview}>
          {t('seva.gigs.krama.review.submit')}
        </button>
      )}

      {current === 'RECYCLED' && (
        <p className="seva-krama-tracker__note seva-krama-tracker__note--recycled">
          🔄 {t('seva.gigs.krama.recycled.note')}
        </p>
      )}

      {current === 'COMPLETE' && (
        <p className="seva-krama-tracker__note seva-krama-tracker__note--complete">
          ✅ {t('seva.gigs.krama.complete.note')}
        </p>
      )}

      {/* Escrow status */}
      <div className="seva-krama-tracker__escrow">
        <span className="seva-krama-tracker__escrow-label">{t('seva.gigs.escrow.label')}:</span>
        <span className={`seva-escrow-badge seva-escrow-badge--${booking.escrow_status.toLowerCase()}`}>
          {booking.escrow_status === 'HELD'     ? `🔒 ${t('seva.gigs.escrow.held')}`
           : booking.escrow_status === 'RELEASED' ? `✅ ${t('seva.gigs.escrow.released')}`
           : `↩️ ${t('seva.gigs.escrow.refunded')}`}
        </span>
      </div>
    </section>
  );
}
